import { useEffect, useState } from "react";

import isEqual from "lodash/isEqual";
import { nanoid } from "nanoid";

import { Box } from "@/components/layout/flexbox";
import { SlotOptions } from "@/components/pieces/coaching/CoachingSettings/timeslots";

import TimeRangeSelector from "./TimeRangeSelector";
import { TimeRange, TimeStop } from "./types";

interface WorkingTimeslotProps {
  date: string;
  loading: boolean;
  slotOptions: SlotOptions;
  timeslots: string[];
  onChange: (timeslots: string[]) => void;
}

export function WorkingTimeslot({
  date,
  loading,
  slotOptions,
  timeslots,
  onChange,
}: WorkingTimeslotProps) {
  const ts: TimeStop[] = timeslots.map((slot) => ({
    index: nanoid(),
    value: slot,
  }));
  const [slots, setSlots] = useState<TimeStop[]>(ts);

  const setTimeRangeInSlot = ({ index, start, end }: TimeRange) => {
    slots.forEach((slot) => {
      if (slot.index === index) {
        slot.value = `${start}-${end}`;
      }
    });

    if (ready) {
      const submitSlots = slots.map((s) => s.value).filter((s) => !!s);
      onChange(submitSlots);
    }
  };

  const removeSlot = (slot: TimeStop) => {
    const newSlots = slots.filter((s) => s.index !== slot.index);
    setSlots(newSlots);
    if (ready) {
      const submitSlots = newSlots.map((s) => s.value).filter((s) => !!s);
      onChange(submitSlots);
    }
  };

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (
      !isEqual(
        timeslots,
        slots.map((s) => s.value),
      )
    ) {
      setSlots(ts);
    }
  }, [timeslots]);

  return (
    <>
      <Box fontWeight={600}>{date}</Box>

      {slots &&
        slots.length > 0 &&
        slots.map((slot) => (
          <Box key={slot.index} maxWidth={350}>
            <TimeRangeSelector
              slotOptions={slotOptions}
              timeRange={slot.value}
              loading={loading}
              onSubmit={({ start, end }) =>
                setTimeRangeInSlot({ index: slot.index, start, end })
              }
              onDelete={() => removeSlot(slot)}
            />
          </Box>
        ))}
      {slots && slots.length === 0 && <Box>Not available</Box>}

      <Box mt={2}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSlots((preSlots) => [
              ...preSlots,
              { value: "", index: nanoid() },
            ]);
          }}
        >
          Add time slot
        </a>
      </Box>
    </>
  );
}
